import { useState } from 'react'
import { formatDateTime } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { Badge, Button, Input, toneStyles, type Tone } from '../ui.js'
import { Label, Panel, PanelHeading } from './panes.js'
import type { Job } from './types.js'

/**
 * What still has to be done on this job.
 *
 * Two lists, and they are not the same thing. The **sections** are the standing
 * instructions for this kind of work, materialised from the template and
 * carrying an audience and a required flag. The **follow-ups** below them are
 * the ad-hoc `Task` rows someone added to this one job. Keeping them apart is
 * what lets a technician be shown a five-item list instead of the
 * nine-and-a-half-item one ServiceM8 showed everybody, two thirds of which went
 * untouched. Decision 41.
 *
 * This is now the *second* block on the Work tab, under the services. It used
 * to stand in for them — a section drawn as a tinted row, because that was the
 * closest thing the schema had to a service. `JobService` is the real one, so
 * the heading says where these came from and the rows have shrunk back to what
 * they are: 549,548 imported items in 119,222 sections, still worked down on
 * site, never back-parsed into a service. Decision 53.
 *
 * Only pinned notes are here. Every note reads in the activity column on
 * Work, so a second full list was the same paragraph twice. A pinned note
 * is different: it is the one somebody wanted read before the van left.
 */
export function ChecklistPane({
  job,
  canEdit,
  officeView,
  isAdding,
  onToggleTask,
  onAddTask,
  onToggleItem,
  onSkipItem,
}: {
  job: Job
  /** Whether the viewer is office rather than a technician on the job. */
  officeView: boolean
  canEdit: boolean
  isAdding: boolean
  onToggleTask: (taskId: string, done: boolean) => void
  onAddTask: (title: string, done: () => void) => void
  onToggleItem: (itemId: string, done: boolean) => void
  onSkipItem: (itemId: string, reason: string, done: () => void) => void
}) {
  const [draft, setDraft] = useState('')

  const done = job.tasks.filter((task) => task.completedAt !== null).length
  const pinned = job.notes.filter((note) => note.isPinned)

  return (
    <>
      <ChecklistSections
        job={job}
        canEdit={canEdit}
        officeView={officeView}
        onToggleItem={onToggleItem}
        onSkipItem={onSkipItem}
      />

      <Panel>
        <PanelHeading
          title="Follow-ups"
          hint={job.tasks.length === 0 ? undefined : `${done} of ${job.tasks.length} done`}
        />

        {job.tasks.length === 0 ? (
          <p className="text-[13px] text-ink-400">Nothing to follow up on this job.</p>
        ) : (
          <ul className="flex flex-col">
            {job.tasks.map((task) => {
              const complete = task.completedAt !== null

              return (
                <li key={task.id}>
                  <label
                    className={cn(
                      'flex items-start gap-2.5 rounded-md px-1.5 py-1.5',
                      canEdit ? 'cursor-pointer hover:bg-ink-50' : 'cursor-default',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={complete}
                      disabled={!canEdit}
                      onChange={(event) => onToggleTask(task.id, event.target.checked)}
                      className="mt-0.5 size-4 shrink-0 rounded-sm accent-brand-600"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span
                        className={cn(
                          'text-[13px]',
                          complete ? 'text-ink-400 line-through' : 'text-ink-900',
                        )}
                      >
                        {task.title}
                      </span>
                      {task.description ? (
                        <span className="text-xs text-ink-500">{task.description}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}

        {canEdit ? (
          <form
            className="flex gap-1.5"
            onSubmit={(event) => {
              event.preventDefault()
              const title = draft.trim()
              if (!title) return
              onAddTask(title, () => setDraft(''))
            }}
          >
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Add a follow-up"
              className="text-[13px]"
            />
            <Button type="submit" loading={isAdding} disabled={draft.trim().length === 0}>
              Add
            </Button>
          </form>
        ) : null}
      </Panel>

      {pinned.length > 0 ? (
        <Panel>
          <PanelHeading title="Pinned" hint="Read this before the van leaves" />
          <ul className="flex flex-col gap-2">
            {pinned.map((note) => (
              <li
                key={note.id}
                className="rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-200 ring-inset"
              >
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-ink-800">
                  {note.body}
                </p>
                <p className="mt-1 text-xs text-ink-500">
                  {note.author?.name ?? 'Someone'} · {formatDateTime(note.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </>
  )
}

type Checklist = NonNullable<Job['checklists']>[number]
type Item = Checklist['items'][number]

/**
 * The job's instructions, by section.
 *
 * A technician is shown the TECH and BOTH items and nothing else, because the
 * office's half of the list is work they cannot do and reading past it is how
 * the items that matter got missed. The office sees everything, with a switch to
 * look at the phone's view — useful when somebody rings up asking what the
 * technician is looking at.
 *
 * A finished section collapses itself. Nine sections open at once is the wall of
 * ticks the audience filter exists to avoid, and the one with work left in it is
 * the one somebody came here for.
 */
function ChecklistSections({
  job,
  canEdit,
  officeView,
  onToggleItem,
  onSkipItem,
}: {
  job: Job
  canEdit: boolean
  officeView: boolean
  onToggleItem: (itemId: string, done: boolean) => void
  onSkipItem: (itemId: string, reason: string, done: () => void) => void
}) {
  const [showAll, setShowAll] = useState(officeView)
  const [skipping, setSkipping] = useState<string | null>(null)
  const [closed, setClosed] = useState<Record<string, boolean>>({})

  const checklists = job.checklists ?? []
  if (checklists.length === 0) return null

  const visible = checklists
    .map((checklist) => ({
      ...checklist,
      items: showAll
        ? checklist.items
        : checklist.items.filter((item) => item.audience !== 'OFFICE'),
    }))
    .filter((checklist) => checklist.items.length > 0)

  if (visible.length === 0) return null

  const shown = visible.flatMap((checklist) => checklist.items)
  const outstanding = shown.filter(
    (item) => item.isRequired && item.completedAt === null && item.skippedAt === null,
  ).length

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2.5">
        <Label>Checklist from ServiceM8</Label>
        <span className="text-[11.5px] text-ink-500">
          {`${shown.filter((item) => item.completedAt !== null).length} of ${shown.length} done`}
        </span>
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="ml-auto text-xs font-semibold text-brand-600 hover:text-brand-700"
        >
          {showAll ? 'Technician view' : 'Show office items'}
        </button>
      </div>

      {outstanding > 0 ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200 ring-inset">
          {outstanding === 1
            ? '1 required item is not done. The visit cannot be completed until it is done or skipped with a reason.'
            : `${outstanding} required items are not done. The visit cannot be completed until they are done or skipped with a reason.`}
        </p>
      ) : null}

      {visible.map((checklist) => {
        const complete = checklist.items.filter((item) => item.completedAt !== null).length
        const isDone = complete === checklist.items.length
        const needed = checklist.items.some(
          (item) => item.isRequired && item.completedAt === null && item.skippedAt === null,
        )
        const tone = isDone ? SECTION_TONE.done : needed ? SECTION_TONE.blocked : SECTION_TONE.open
        const open = closed[checklist.id] ?? !isDone

        return (
          <div key={checklist.id} className="flex flex-col gap-0.5">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setClosed((current) => ({ ...current, [checklist.id]: !open }))}
              className={cn(
                'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left ring-1 ring-inset',
                toneStyles[tone.tone],
              )}
            >
              <span
                className={cn('grid size-5 shrink-0 place-items-center rounded-md', tone.tile)}
              >
                <Icon name={isDone ? 'check' : 'jobs'} className="size-3 text-white" />
              </span>
              <span className="min-w-0 truncate text-[13px] font-semibold">{checklist.name}</span>
              <span className="ml-auto shrink-0 text-[11.5px] opacity-75">
                {isDone
                  ? 'Done'
                  : `${complete}/${checklist.items.length}${needed ? ' · required left' : ''}`}
              </span>
              <Icon
                name="forward"
                className={cn(
                  'size-3.5 shrink-0 opacity-60 transition-transform',
                  open && 'rotate-90',
                )}
              />
            </button>

            {open ? (
              <ul className="flex flex-col">
                {checklist.items.map((item) => (
                  <li key={item.id}>
                    <ChecklistRow
                      item={item}
                      canEdit={canEdit}
                      isSkipping={skipping === item.id}
                      onToggle={(done) => onToggleItem(item.id, done)}
                      onStartSkip={() => setSkipping(item.id)}
                      onCancelSkip={() => setSkipping(null)}
                      onSkip={(reason) => onSkipItem(item.id, reason, () => setSkipping(null))}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Three states a section can be in, as the mockup's tinted rows.
 *
 * Green is finished, amber is holding a visit up, and blue is ordinary work in
 * progress — the same three tones a badge would use, through `toneStyles`,
 * rather than a fourth private palette. Only the solid tile is extra, and it is
 * the darker step of the same hue so the row reads from arm's length, which is
 * what the tinting is for.
 */
const SECTION_TONE = {
  done: { tone: 'green', tile: 'bg-emerald-600' },
  blocked: { tone: 'amber', tile: 'bg-amber-700' },
  open: { tone: 'blue', tile: 'bg-blue-700' },
} satisfies Record<string, { tone: Tone; tile: string }>

const ITEM_TYPE_LABEL: Record<string, string> = {
  PHOTO: 'Photo',
  FORM: 'Form',
  ASSET: 'Asset',
}

function ChecklistRow({
  item,
  canEdit,
  isSkipping,
  onToggle,
  onStartSkip,
  onCancelSkip,
  onSkip,
}: {
  item: Item
  canEdit: boolean
  isSkipping: boolean
  onToggle: (done: boolean) => void
  onStartSkip: () => void
  onCancelSkip: () => void
  onSkip: (reason: string) => void
}) {
  const [reason, setReason] = useState('')

  const complete = item.completedAt !== null
  const skipped = item.skippedAt !== null
  const typeLabel = ITEM_TYPE_LABEL[item.itemType]

  return (
    <div className="rounded-md px-1.5 py-1 hover:bg-white">
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={complete}
          disabled={!canEdit}
          aria-label={item.text}
          onChange={(event) => onToggle(event.target.checked)}
          className="mt-0.5 size-4 shrink-0 rounded-sm accent-brand-600"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                'text-[13px]',
                complete ? 'text-ink-400 line-through' : skipped ? 'text-ink-500' : 'text-ink-900',
              )}
            >
              {item.text}
            </span>
            {item.isRequired && !complete && !skipped ? (
              <Badge tone="amber">Required</Badge>
            ) : null}
            {typeLabel ? <Badge tone="neutral">{typeLabel}</Badge> : null}
            {item.audience === 'OFFICE' ? <Badge tone="neutral">Office</Badge> : null}
          </span>

          {skipped ? (
            <span className="text-xs text-amber-700">
              Skipped: {item.skipReason}
              {item.skippedBy ? ` · ${item.skippedBy.name}` : ''}
            </span>
          ) : null}
          {complete && item.completedBy ? (
            <span className="text-xs text-ink-500">
              {item.completedBy.name} · {formatDateTime(item.completedAt)}
            </span>
          ) : null}
        </span>

        {/* Only required items offer a skip. Anything else is simply left
            unticked, and asking for a reason to not do optional work is the
            kind of ceremony that gets typed "n/a" nine thousand times. */}
        {canEdit && item.isRequired && !complete && !skipped && !isSkipping ? (
          <button
            type="button"
            onClick={onStartSkip}
            className="shrink-0 text-xs font-medium text-ink-500 hover:text-ink-800"
          >
            Skip
          </button>
        ) : null}
      </div>

      {isSkipping ? (
        <form
          className="mt-1.5 flex gap-1.5 pl-6"
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = reason.trim()
            if (trimmed.length < 3) return
            onSkip(trimmed)
          }}
        >
          <Input
            value={reason}
            autoFocus
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this being skipped?"
            className="text-[13px]"
          />
          <Button type="submit" disabled={reason.trim().length < 3}>
            Skip
          </Button>
          <Button type="button" variant="ghost" onClick={onCancelSkip}>
            Cancel
          </Button>
        </form>
      ) : null}
    </div>
  )
}
